import { createTheme } from "@mui/material/styles";
import { withMotion } from "./motion";

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  THEME CENTRAL — TODOS los colores de la app viven acá                  ║
 * ╠══════════════════════════════════════════════════════════════════════════╣
 * ║                                                                          ║
 * ║  CÓMO REFERENCIAR un color desde un componente:                         ║
 * ║                                                                          ║
 * ║    1. sx prop (preferido):                                              ║
 * ║         sx={{ backgroundColor: "var(--bg-elevated)" }}                  ║
 * ║                                                                          ║
 * ║    2. CSS file:                                                          ║
 * ║         .mi-clase { background: var(--bg-elevated); }                   ║
 * ║                                                                          ║
 * ║    3. Canvas / lib JS que NO acepta var() (ej. ctx.fillStyle):          ║
 * ║         const v = getComputedStyle(document.documentElement)            ║
 * ║             .getPropertyValue("--bg-elevated").trim();                  ║
 * ║       (ver DxGraphCanvas.js para helper `cssVar`)                       ║
 * ║                                                                          ║
 * ║    4. Theme MUI (useTheme):                                              ║
 * ║         theme.palette.background.elevated                               ║
 * ║         theme.palette.tokens["bg-elevated"]                             ║
 * ║                                                                          ║
 * ║  CÓMO AGREGAR un color nuevo:                                            ║
 * ║                                                                          ║
 * ║    1. NUNCA hardcodear un hex en un componente.                         ║
 * ║    2. Agregar la entrada al objeto `tokens.dark` Y `tokens.light`       ║
 * ║       con el MISMO key. La paridad se valida con:                       ║
 * ║         node scripts/validate-theme.js                                  ║
 * ║    3. Usar el nuevo `var(--mi-token)` en el componente.                 ║
 * ║                                                                          ║
 * ║  REGLAS DE NOMING (semánticas, no por color):                            ║
 * ║    - bg-*       fondos de la app (base, elevated, hover...)             ║
 * ║    - text-*     colores de texto (primary, secondary, muted...)         ║
 * ║    - color-*    semánticos (primary, success, warning, danger...)       ║
 * ║    - accent-*   acentos decorativos (sky, violet, emerald...)           ║
 * ║    - sys-*      identidad médica por sistema anatómico                  ║
 * ║    - cat-*      categorías de exámenes (lab, img, electro...)           ║
 * ║    - paper-*    documentos imprimibles (NO se invierten)                ║
 * ║    - fg-rgb / shadow-rgb  triplets RGB para `rgba(var(--rgb), 0.X)`     ║
 * ║                                                                          ║
 * ║  El theme `dark` produce los colores originales (regresión cero).       ║
 * ║  El theme `light` invierte fondos/textos y oscurece acentos para        ║
 * ║  mantener contraste sobre blanco.                                        ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

const tokens = {
  dark: {
    // Backgrounds (de más profundo a más elevado)
    "bg-deep": "#080a0e",
    // Tarjetas de "foto" del estilo E (landing, acceso): SIEMPRE oscuras, en
    // los dos modos — mismo valor en dark y light a propósito (#1439).
    "media-bg": "#05080b",
    "media-spot-rgb": "80, 170, 210",
    // Acento de las rayitas/íconos/enlaces del onboarding (#1525 R18). Es el
    // `--accent` del mockup: teal suave en oscuro, azul profundo en claro (el
    // `color-primary-soft` no cambia de modo y en claro no da el contraste).
    "accent-onboarding": "#508AA8",
    // Acento de la ficha 360 de /admin/users (#1606, R38): es el `--accent-soft` del mockup
    // `smartdx_mockups_admin_user_360.html` (degradado del avatar, píldora "pri", ícono del
    // veredicto). NO es el `accent-soft` de este archivo (azul claro, otro color): se llama distinto
    // para que nadie lo "unifique". Teal suave en oscuro, el teal de marca en claro.
    "accent-ficha": "#508AA8",
    // Pop-up "llévala a tu celular" (#1686, R42): `qr`/`qr-bg` son los módulos y el fondo del QR; el
    // fondo es blanco en AMBOS modos a propósito (un QR invertido no lo leen todas las cámaras) y el
    // módulo oscuro cambia solo de tono. `wa` es el verde del botón "Mándamelo por WhatsApp" (texto y
    // borde; el fondo translúcido sale de `color-success-rgb`).
    "qr": "#0a0c10",
    "qr-bg": "#ffffff",
    "wa": "#4ade80",
    "bg-base": "#0a0c10",
    "bg-elevated": "#141416",
    "bg-elevated-rgb": "20, 20, 22",
    "bg-input": "#222225",
    "bg-accordion": "#1c2029",
    "bg-hover": "#3a3f4b",
    "bg-active": "#3e4047",
    "bg-surface": "#242a36",
    "bg-button": "#4a5568",
    // Brand
    "color-primary": "#1F6F97",
    "color-primary-soft": "#508AA8",
    "color-secondary": "#FCF0CC",
    "color-secondary-rgb": "252, 240, 204",
    // Text
    "text-primary": "#FFFFFF",
    "text-primary-soft": "#f8fafc",
    "text-secondary": "#e2e8f0",
    "text-tertiary": "#cbd5e1",
    "text-muted": "#94a3b8",
    "text-muted-rgb": "148, 163, 184",
    "text-faint": "#64748b",
    "text-dim": "#475569",
    "text-fainter": "#334155",
    "text-on-light": "#1A1A1A",
    "black-pure": "#000000",
    // Visor de cámara (#575, foto → PREVIO): negro con controles blancos en los
    // DOS modos, como la app Cámara; un visor claro deja la hoja sin contraste.
    "camera-bg": "#000000",
    "camera-fg": "#ffffff",
    "camera-fg-rgb": "255, 255, 255",
    // Grays neutros legacy
    "gray-mid": "#888",
    "gray-faint": "#9aa0a8",
    // Borders / dividers
    "border-soft": "rgba(255,255,255,0.08)",
    "border-strong": "rgba(255,255,255,0.16)",
    // Semantic
    "color-success": "#4ade80",
    "color-success-dark": "#2e7d32",
    // Triplets RGB de semánticos → para rgba(var(--x-rgb), 0.X) (tints/borders).
    // text-on-success = texto sobre botón success (invierte con el modo:
    // dark = verde brillante → texto oscuro; light = verde oscuro → texto blanco).
    "color-success-rgb": "74, 222, 128",
    "color-danger-rgb": "248, 113, 113",
    "color-primary-rgb": "31, 111, 151",
    "color-info-rgb": "130, 177, 255",
    "color-warning-rgb": "251, 191, 36",
    "text-on-success": "#06210f",
    // text-on-danger = texto sobre botón danger. Misma lógica que
    // text-on-success: en dark el rojo es brillante (#f87171) y pide texto
    // oscuro; en light el rojo es profundo (#b91c1c) y pide blanco.
    "text-on-danger": "#2a0707",
    // text-on-primary = texto sobre fondo primary (teal #1F6F97): blanco en
    // ambos modos (el teal no se invierte — mismo triplet en dark y light).
    "text-on-primary": "#ffffff",
    "color-warning": "#fbbf24",
    "color-warning-soft": "#fde68a",
    // Texto de advertencia legible sobre superficie (consejos): ámbar claro en dark.
    // #1520 D7: ≥ 4.5:1 en los dos temas (soft vale #fde68a en light y no se lee).
    "color-warning-text": "#fde68a",
    "color-warning-bright": "#ff9906",
    "color-warning-orange": "#fb923c",
    "color-danger": "#f87171",
    "color-danger-strong": "#ef4444",
    "color-danger-soft": "#fca5a5",
    "color-danger-deep": "#D62246",
    "color-danger-deep-rgb": "214, 34, 70",
    "color-info": "#82b1ff",
    "color-info-strong": "#60a5fa",
    "color-info-soft": "#4a7fbf",
    // Temperature scale (mantener legacy)
    "temp-high": "#F34D67",
    "temp-medium": "#F17D4E",
    // Triplets RGB para tintes (píldora de calificación, #1679 R45): mismos colores, componibles con rgba().
    "temp-high-rgb": "243, 77, 103",
    "temp-medium-rgb": "241, 125, 78",
    "temp-low": "#ECF226",
    "temp-high-dark": "#D62246",
    "temp-medium-dark": "#D17C4E",
    "temp-low-dark": "#C9D226",
    // Overlay para body::before gradient
    "overlay-glow-a": "rgba(31, 111, 151, 0.16)",
    "overlay-glow-b": "rgba(130, 177, 255, 0.05)",
    // RGB triplets para componer rgba(var(--fg-rgb), 0.X) — invertidos por modo
    "fg-rgb": "255, 255, 255",
    "shadow-rgb": "0, 0, 0",
    // Comunidad (#1301, R28): el globo de colegas. Valores del mockup elegido
    // (smartdx_mockups_colegas_seccion_v2): esfera, brillo de los países según su
    // cantidad de cuentas, acento propio (el `color-primary-rgb` no cambia de modo y el
    // del globo sí) y opacidad de la tierra. El canvas los lee con getComputedStyle.
    "globo-esfera-a": "#1a2029",
    "globo-esfera-b": "#0b0e13",
    "globo-brillo-bajo-rgb": "31, 111, 151",
    "globo-brillo-alto-rgb": "130, 177, 255",
    "globo-acento-rgb": "31, 111, 151",
    "globo-tierra-alfa": "0.20",
    // Triplet para SUPERFICIES glass (panels/tooltips/sheets flotantes que
    // se componen con rgba(var(--surface-rgb), 0.X)). En dark = negro (idéntico
    // a shadow-rgb → regresión cero); en light = blanco (la superficie se
    // vuelve blanco translúcido en vez de navy oscuro). NO usar para boxShadow
    // ni para backdrops de modales — esos van con shadow-rgb (oscuros siempre).
    "surface-rgb": "0, 0, 0",
    // Accent extras (idénticos en ambos modos por default — son colores
    // de estado/decoración que mantienen identidad visual)
    "bg-slate-800": "#1f2937",
    "bg-slate-900": "#111827",
    "bg-slate-800-alt": "#1e293b",
    "text-slate-50": "#f9fafb",
    "text-slate-200-alt": "#e5e7eb",
    "gray-500": "#6b7280",
    "gray-600": "#4b5563",
    "gray-light": "#e0e0e0",
    "slate-dark": "#33424c",
    "accent-sky": "#38bdf8",
    "accent-soft": "#82b1ff",
    "accent-emerald": "#34d399",
    "accent-green": "#22c55e",
    "accent-green-strong": "#16a34a",
    "accent-green-strong-rgb": "22, 163, 74",
    "accent-green-soft": "#52b788",
    "accent-green-200": "#bbf7d0",
    "accent-sky-200": "#bae6fd",
    "accent-teal-200": "#b2dfdb",
    "accent-indigo-300": "#a5b4fc",
    "accent-indigo-400": "#818cf8",
    "accent-violet": "#a78bfa",
    "accent-purple": "#c084fc",
    "accent-pink": "#f472b6",
    "accent-amber-300": "#fcd34d",
    "accent-amber": "#f59e0b",
    "accent-amber-rgb": "245, 158, 11",
    "accent-red-700": "#c53030",
    "accent-green-700": "#2f855a",
    "accent-green-800": "#276749",
    // Backgrounds alternates (variantes del bg-elevated/deep para shadows o cards)
    "bg-deep-alt": "#0c0e12",
    "bg-deep-alt2": "#0f1218",
    "bg-deep-alt3": "#0f172a",
    "bg-elevated-alt": "#141416",
    "bg-elevated-alt2": "#222225",
    "bg-elevated-alt3": "#373c47",
    "bg-elevated-cra": "#141416",
    "bg-surface-alt": "#515864",
    "bg-slate-900-alt": "#1f1f1f",
    "bg-slate-900-alt2": "#111",
    // Grays adicionales
    "gray-bg-light": "#e5e5e5",
    "gray-mid-dark": "#3b4252",
    "gray-lighter": "#cccccc",
    "text-tertiary-alt": "#cfcfcf",
    "text-tertiary-alt2": "#cfd4da",
    "text-muted-alt": "#999",
    "text-faint-alt": "#666",
    "text-faint-alt2": "#555",
    // Semantic adicionales
    "color-danger-darker": "#dc2626",
    "color-danger-bright": "#ff6b6b",
    "color-warning-amber": "#fbc02d",
    "color-warning-yellow": "#eab308",
    "color-warning-orange-bright": "#f97316",
    "accent-violet-light": "#c4b5fd",
    "accent-violet-light-rgb": "196, 181, 253",
    "accent-cyan": "#67e8f9",
    "accent-cyan-rgb": "103, 232, 249",
    "accent-blue": "#3b82f6",
    "accent-sky-strong": "#0284c7",
    "accent-sky-mid": "#0ea5e9",
    "accent-violet-strong": "#7c3aed",
    "accent-blue-strong": "#1d4ed8",
    "accent-green-700-alt": "#15803d",
    "accent-green-800-alt": "#166534",
    // Paper tokens: representan documentos imprimibles (indicaciones, recetas).
    // Permanecen blanco/negro en AMBOS modos — son hojas, no UI.
    "paper-bg": "#ffffff",
    "paper-text": "#1A1A1A",
    "paper-border": "#cccccc",
    "paper-border-strong": "#222222",
    // Popover/hover signature SmartDx (estilo de diseño aprobado por founder)
    // — oscuro casi negro con alpha ~0.88 para que el `backdrop-filter:
    // blur(24px)` tenga efecto visible (sin alpha el bg sólido tapa el blur).
    // Combinado con `backgroundImage: none` en el Paper del Popover para
    // desactivar el overlay claro de MUI dark elevation.
    // Ver styles/design-tokens.md sección "Hover / Popover signature".
    "popover-bg": "rgba(4, 6, 10, 0.60)",
    // v5 — Backgrounds darks remanentes
    "bg-deep-alt4": "#14171d",
    "bg-deep-alt5": "#161a20",
    "bg-deep-alt6": "#181b21",
    "bg-elevated-deeper": "#1C1C1E",
    "bg-elevated-alt4": "#222225",
    "bg-elevated-alt5": "#50545C",
    // v5 — Accents
    "accent-blue-dark": "#0f4c75",
    "accent-google-blue": "#4285F4",
    "accent-blue-mid": "#2980b9",
    "accent-yellow-bright": "#F1F73E",
    // v5 — System colors (paleta médica por sistema anatómico)
    "sys-respiratorio": "#22d3ee",
    "sys-hematologica": "#e879f9",
    "sys-dermatologia": "#fb7185",
    "sys-obstetricia": "#fda4af",
    "sys-neonatologia": "#93c5fd",
    "sys-geriatria": "#d1d5db",
    "sys-oftalmologia": "#f0abfc",
    "sys-toxicologia": "#facc15",
    "sys-anestesia": "#a3a3a3",
    "origin-inherited-inverse": "#a30f8d",
    // v5 — Status
    "text-tertiary-alt3": "#AAAAAA",
    "color-danger-soft-alt": "#E57373",
    "color-danger-bright-alt": "#FF5252",
    "color-danger-deep-alt": "#b71c1c",
    "color-danger-bg-light": "#fff5f5",
    "color-danger-border-light": "#e0b4b4",
    "color-success-border-light": "#b4d8b4",
    "color-success-bg-light": "#f5faf5",
    "accent-green-soft-alt": "#a8e0b4",
    "accent-green-soft-alt-rgb": "168, 224, 180",
    "accent-green-bg-soft": "#f3f7f0",
    // v5 — Categorías de exámenes (TopExams)
    "cat-lab": "#d32f2f",
    "cat-img": "#1976d2",
    "cat-electro": "#fdd835",
    "cat-micro": "#388e3c",
    "cat-otro": "#757575",
    "cat-orina": "#f57c00",
    "cat-purple": "#8e24aa",
    "cat-teal": "#00897b",
    "cat-brown": "#6d4c41",
    "cat-pink": "#d81b60",
    // #1422 — color por posición de sección del checklist de Actualización
    // Diaria (Default, Coronaria y propias con keys inventadas se pintan por
    // ÍNDICE, no por key: `colorDeSeccion` en utils/plantillaActualizacion.js).
    // Mismos 12 colores que `SECTION_COLOR` tenía hardcodeados (regresión cero).
    "seccion-1": "#82b1ff",
    "seccion-2": "#06b6d4",
    "seccion-3": "#22d3ee",
    "seccion-4": "#fbbf24",
    "seccion-5": "#f87171",
    "seccion-6": "#38bdf8",
    "seccion-7": "#4ade80",
    "seccion-8": "#fb923c",
    "seccion-9": "#a78bfa",
    "seccion-10": "#dc2626",
    "seccion-11": "#f472b6",
    "seccion-12": "#c084fc",
    // Final pass — tokens adicionales detectados en pasada final
    "accent-teal-bright": "#00CBA7",
    "accent-teal-bg": "#e0f2f1",
    "accent-teal-mid": "#75cbc4",
    "accent-blue-deep": "#135273",
    "color-warning-dark-alt": "#b56400",
    "color-success-soft": "#86efac",
    "text-tertiary-alt4": "#B0B0B0",
    "bg-deep-alt7": "#15171b",
    "bg-deep-alt8": "#0f1117",
    "bg-deep-alt9": "#0f1217",
    "pastel-violet": "#ddd6fe",
    "pastel-blue": "#bfdbfe",
    "pastel-red": "#fecaca",
    "text-faint-alt3": "#777",
    "text-on-light-alt": "#333",
    // Turno-specific state colors (sidebar y cockpit mobile)
    "turno-state-error": "#f1a4b0",
    "turno-state-warn": "#f1c46a",
    "turno-state-warn-rgb": "241, 196, 106",
    "turno-state-error-hover": "#b71c3a",
    "turno-cell-bg": "#2c3138",
    "color-primary-hover": "#266d8d",
    // Acento "académico" — dudas de estudio en el turno. Índigo, fuera de la
    // paleta clínica (teal/rojo/ámbar) para que se lea como NO-clínico.
    "turno-academic": "#a78bfa",
    "turno-academic-rgb": "167, 139, 250",
    // Texto sobre el índigo académico (badges): en dark el índigo es claro →
    // texto oscuro; en light el índigo es oscuro → texto blanco.
    "text-on-academic": "#241b3a",
    // #1744 · marca de "mis pacientes": selección amarilla de MisPacientesDialog. Decisión de Sebastián
    // (18-oct-2026): oscuro #eab308, claro #ca8a04 (más amarillo que el ámbar de `turno-state-warn`).
    // #1840: la LISTA de camas (computador y celular) ya NO los usa, "tuyo" ahora es un bloque con título
    // (R4); se quedan solo para el diálogo (R71 de #1744, fuera de alcance de #1840).
    // Cubre R71 — .claude/specs/1744-pegar-documento-camas-previo/
    "turno-mine-rail": "#eab308",
    "turno-mine-tint-rgb": "234, 179, 8",
    // #1840 R6 · texto (número de cama y ✓) de una fila con evolución lista. En oscuro es el verde de siempre
    // (= accent-green-soft-alt); en claro ese verde da ~2,3:1 sobre blanco y se pasa a `color-success`
    // (~5:1). El riel y el degradado siguen con accent-green-soft-alt en los dos temas.
    // Cubre R6 — .claude/specs/1840-marcas-lista-v4/
    "turno-state-ok-fg": "#a8e0b4",
  },
  light: {
    // Slate Refined: jerarquía invertida — el lienzo (bg-base) es off-white
    // (#f8fafc, reduce glare/fatiga en turnos largos) y las superficies
    // elevadas (bg-elevated = cards/paneles) son blanco puro y "flotan".
    "bg-deep": "#e2e8f0",
    // Tarjetas de "foto" del estilo E (landing, acceso): SIEMPRE oscuras, en
    // los dos modos — mismo valor en dark y light a propósito (#1439).
    "media-bg": "#05080b",
    "media-spot-rgb": "80, 170, 210",
    "accent-onboarding": "#1558a8",
    "accent-ficha": "#1F6F97",
    // Ver el comentario de `tokens.dark` (#1686, R42).
    "qr": "#0f172a",
    "qr-bg": "#ffffff",
    "wa": "#15803d",
    "bg-base": "#f8fafc",
    "bg-elevated": "#ffffff",
    "bg-elevated-rgb": "255, 255, 255",
    "bg-input": "#f1f5f9",
    "bg-accordion": "#e2e8f0",
    "bg-hover": "#e2e8f0",
    "bg-active": "#cbd5e1",
    "bg-surface": "#cbd5e1",
    "bg-button": "#cbd5e1",
    "color-primary": "#1F6F97",
    "color-primary-soft": "#508AA8",
    "color-secondary": "#b8860b",
    "color-secondary-rgb": "184, 134, 11",
    "text-primary": "#0f172a",
    "text-primary-soft": "#0f172a",
    "text-secondary": "#334155",
    "text-tertiary": "#475569",
    "text-muted": "#475569",
    "text-muted-rgb": "71, 85, 105",
    "text-faint": "#64748b",
    "text-dim": "#94a3b8",
    "text-fainter": "#cbd5e1",
    "text-on-light": "#1A1A1A",
    "black-pure": "#000000",
    "camera-bg": "#000000",
    "camera-fg": "#ffffff",
    "camera-fg-rgb": "255, 255, 255",
    "gray-mid": "#64748b",
    "gray-faint": "#64748b",
    "border-soft": "rgba(15,23,42,0.08)",
    "border-strong": "rgba(15,23,42,0.16)",
    "color-success": "#15803d",
    "color-success-dark": "#166534",
    // Triplets RGB (light: oscurecidos para contraste sobre blanco). Paridad con dark.
    "color-success-rgb": "21, 128, 61",
    "color-danger-rgb": "185, 28, 28",
    "color-primary-rgb": "31, 111, 151",
    "color-info-rgb": "37, 99, 235",
    "color-warning-rgb": "180, 83, 9",
    "text-on-success": "#ffffff",
    "text-on-danger": "#ffffff",
    "text-on-primary": "#ffffff",
    "color-warning": "#b45309",
    "color-warning-soft": "#fde68a",
    "color-warning-text": "#92400e",
    "color-warning-bright": "#ea580c",
    "color-warning-orange": "#c2410c",
    "color-danger": "#b91c1c",
    "color-danger-strong": "#991b1b",
    "color-danger-soft": "#fca5a5",
    "color-danger-deep": "#D62246",
    "color-danger-deep-rgb": "214, 34, 70",
    "color-info": "#2563eb",
    "color-info-strong": "#1d4ed8",
    "color-info-soft": "#1e50a8",
    "temp-high": "#F34D67",
    "temp-medium": "#F17D4E",
    // Triplets RGB para tintes (píldora de calificación, #1679 R45): mismos colores, componibles con rgba().
    "temp-high-rgb": "243, 77, 103",
    "temp-medium-rgb": "241, 125, 78",
    "temp-low": "#ECF226",
    "temp-high-dark": "#D62246",
    "temp-medium-dark": "#D17C4E",
    "temp-low-dark": "#C9D226",
    "overlay-glow-a": "rgba(31, 111, 151, 0.10)",
    "overlay-glow-b": "rgba(130, 177, 255, 0.04)",
    "fg-rgb": "15, 23, 42",
    "shadow-rgb": "15, 23, 42",
    // Comunidad (#1301, R28): el globo de colegas. Valores del mockup elegido
    // (smartdx_mockups_colegas_seccion_v2): esfera, brillo de los países según su
    // cantidad de cuentas, acento propio (el `color-primary-rgb` no cambia de modo y el
    // del globo sí) y opacidad de la tierra. El canvas los lee con getComputedStyle.
    "globo-esfera-a": "#ffffff",
    "globo-esfera-b": "#dfe7ef",
    "globo-brillo-bajo-rgb": "80, 138, 168",
    "globo-brillo-alto-rgb": "26, 95, 130",
    "globo-acento-rgb": "26, 95, 130",
    "globo-tierra-alfa": "0.22",
    "surface-rgb": "255, 255, 255",
    // Accent extras: en light invertimos los slate (que eran fondos oscuros
    // accent) a fondos claros, y oscurecemos los amarillos/verdes claros
    // para que mantengan legibilidad en fondo blanco.
    "bg-slate-800": "#e2e8f0",
    "bg-slate-900": "#cbd5e1",
    "bg-slate-800-alt": "#e2e8f0",
    "text-slate-50": "#1A1A1A",
    "text-slate-200-alt": "#334155",
    "gray-500": "#64748b",
    "gray-600": "#475569",
    "gray-light": "#cbd5e1",
    "slate-dark": "#cbd5e1",
    "accent-sky": "#0284c7",
    "accent-soft": "#1565c0",
    "accent-emerald": "#059669",
    "accent-green": "#16a34a",
    "accent-green-strong": "#15803d",
    "accent-green-strong-rgb": "21, 128, 61",
    "accent-green-soft": "#16a34a",
    "accent-green-200": "#dcfce7",
    "accent-sky-200": "#e0f2fe",
    "accent-teal-200": "#ccfbf1",
    "accent-indigo-300": "#6366f1",
    "accent-indigo-400": "#4f46e5",
    "accent-violet": "#7c3aed",
    "accent-purple": "#9333ea",
    "accent-pink": "#db2777",
    "accent-amber-300": "#d97706",
    "accent-amber": "#b45309",
    "accent-amber-rgb": "180, 83, 9",
    "accent-red-700": "#b91c1c",
    "accent-green-700": "#15803d",
    "accent-green-800": "#14532d",
    // Backgrounds alternates en light invierten a tonos claros para que cards
    // y paneles que usaban estos hex como "fondo oscuro" pasen a "fondo claro"
    "bg-deep-alt": "#f1f5f9",
    "bg-deep-alt2": "#f8fafc",
    "bg-deep-alt3": "#f1f5f9",
    "bg-elevated-alt": "#f8fafc",
    "bg-elevated-alt2": "#f1f5f9",
    "bg-elevated-alt3": "#e2e8f0",
    "bg-elevated-cra": "#f8fafc",
    "bg-surface-alt": "#cbd5e1",
    "bg-slate-900-alt": "#e2e8f0",
    "bg-slate-900-alt2": "#cbd5e1",
    "gray-bg-light": "#f1f5f9",
    "gray-mid-dark": "#475569",
    "gray-lighter": "#cbd5e1",
    "text-tertiary-alt": "#475569",
    "text-tertiary-alt2": "#475569",
    "text-muted-alt": "#64748b",
    "text-faint-alt": "#475569",
    "text-faint-alt2": "#334155",
    "color-danger-darker": "#b91c1c",
    "color-danger-bright": "#dc2626",
    "color-warning-amber": "#d97706",
    "color-warning-yellow": "#a16207",
    "color-warning-orange-bright": "#c2410c",
    "accent-violet-light": "#7c3aed",
    "accent-violet-light-rgb": "124, 58, 237",
    "accent-cyan": "#0891b2",
    "accent-cyan-rgb": "8, 145, 178",
    "accent-blue": "#2563eb",
    "accent-sky-strong": "#0369a1",
    "accent-sky-mid": "#0284c7",
    "accent-violet-strong": "#6d28d9",
    "accent-blue-strong": "#1e40af",
    "accent-green-700-alt": "#15803d",
    "accent-green-800-alt": "#14532d",
    // Paper tokens: idénticos en ambos modos por diseño (documento físico).
    "paper-bg": "#ffffff",
    "paper-text": "#1A1A1A",
    "paper-border": "#cccccc",
    "paper-border-strong": "#222222",
    // Popover signature en light — blanco semitrans con blur (mantiene
    // glass look, contrasta sobre el bg-base claro de la vista).
    "popover-bg": "rgba(255, 255, 255, 0.96)",
    // v5 — Backgrounds: en light invertimos a tonos claros sutiles
    "bg-deep-alt4": "#e2e8f0",
    "bg-deep-alt5": "#f1f5f9",
    "bg-deep-alt6": "#f1f5f9",
    "bg-elevated-deeper": "#f8fafc",
    "bg-elevated-alt4": "#f1f5f9",
    "bg-elevated-alt5": "#cbd5e1",
    // v5 — Accents: usan los mismos colores que dark (son identidad visual)
    "accent-blue-dark": "#0f4c75",
    "accent-google-blue": "#4285F4",
    "accent-blue-mid": "#2980b9",
    "accent-yellow-bright": "#a16207",
    // v5 — System colors: oscurecemos los pasteles para legibilidad sobre blanco
    "sys-respiratorio": "#0891b2",
    "sys-hematologica": "#a21caf",
    "sys-dermatologia": "#e11d48",
    "sys-obstetricia": "#be123c",
    "sys-neonatologia": "#1d4ed8",
    "sys-geriatria": "#64748b",
    "sys-oftalmologia": "#a21caf",
    "sys-toxicologia": "#a16207",
    "sys-anestesia": "#525252",
    "origin-inherited-inverse": "#86198f",
    // v5 — Status
    "text-tertiary-alt3": "#64748b",
    "color-danger-soft-alt": "#dc2626",
    "color-danger-bright-alt": "#b91c1c",
    "color-danger-deep-alt": "#991b1b",
    "color-danger-bg-light": "#fff5f5",
    "color-danger-border-light": "#fca5a5",
    "color-success-border-light": "#86efac",
    "color-success-bg-light": "#f0fdf4",
    "accent-green-soft-alt": "#22c55e",
    "accent-green-soft-alt-rgb": "34, 197, 94",
    "accent-green-bg-soft": "#f0fdf4",
    // v5 — Categorías de exámenes (mantienen identidad pero se oscurecen leve)
    "cat-lab": "#b91c1c",
    "cat-img": "#1d4ed8",
    "cat-electro": "#a16207",
    "cat-micro": "#15803d",
    "cat-otro": "#475569",
    "cat-orina": "#c2410c",
    "cat-purple": "#7e22ce",
    "cat-teal": "#0f766e",
    "cat-brown": "#5e3925",
    "cat-pink": "#be185d",
    // #1422 — colores de sección (oscurecidos para contraste sobre blanco;
    // mismo key que en dark, ver comentario ahí).
    "seccion-1": "#1d4ed8",
    "seccion-2": "#0e7490",
    "seccion-3": "#0891b2",
    "seccion-4": "#b45309",
    "seccion-5": "#b91c1c",
    "seccion-6": "#0369a1",
    "seccion-7": "#15803d",
    "seccion-8": "#c2410c",
    "seccion-9": "#6d28d9",
    "seccion-10": "#991b1b",
    "seccion-11": "#be185d",
    "seccion-12": "#7e22ce",
    // Final pass — light counterparts (oscurecidos para legibilidad sobre blanco)
    "accent-teal-bright": "#0d9488",
    "accent-teal-bg": "#ccfbf1",
    "accent-teal-mid": "#0f766e",
    "accent-blue-deep": "#075985",
    "color-warning-dark-alt": "#92400e",
    "color-success-soft": "#15803d",
    "text-tertiary-alt4": "#64748b",
    "bg-deep-alt7": "#f8fafc",
    "bg-deep-alt8": "#f1f5f9",
    "bg-deep-alt9": "#f1f5f9",
    "pastel-violet": "#c4b5fd",
    "pastel-blue": "#93c5fd",
    "pastel-red": "#fca5a5",
    "text-faint-alt3": "#64748b",
    "text-on-light-alt": "#1A1A1A",
    // Turno-specific states en light (oscurecidos)
    "turno-state-error": "#b91c1c",
    "turno-state-warn": "#b45309",
    "turno-state-warn-rgb": "180, 83, 9",
    "turno-state-error-hover": "#7f1d1d",
    "turno-cell-bg": "#f1f5f9",
    "color-primary-hover": "#0f5070",
    // Acento "académico" — versión oscurecida para contraste sobre blanco.
    "turno-academic": "#6d5ce0",
    "turno-academic-rgb": "109, 92, 224",
    // Texto sobre el índigo académico (badges): índigo oscuro → texto blanco.
    "text-on-academic": "#ffffff",
    // #1744 · mismo par que en oscuro; claro #ca8a04 (decisión de Sebastián, 18-oct-2026).
    "turno-mine-rail": "#ca8a04",
    "turno-mine-tint-rgb": "202, 138, 4",
    // #1840 R6 · ver el comentario de oscuro (= color-success claro).
    "turno-state-ok-fg": "#15803d",
  },
};

export const THEME_TOKENS = tokens;

/**
 * Aplica los tokens del modo activo a CSS custom properties en :root
 * para que los archivos CSS y los `sx` props con `var(--xxx)` resuelvan
 * contra la paleta correcta.
 */
export const applyThemeVars = (mode) => {
  const palette = tokens[mode] || tokens.dark;
  const root = document.documentElement;
  for (const [key, value] of Object.entries(palette)) {
    root.style.setProperty(`--${key}`, value);
  }
  root.setAttribute("data-theme", mode);
  // Hint al user-agent para form controls nativos (scrollbar, etc.)
  root.style.colorScheme = mode;
};

/**
 * Construye el theme MUI para el modo dado. Expone los mismos tokens en
 * `palette` para que useTheme() los pueda leer desde JS (canvas, charts).
 */
export const createAppTheme = (mode = "dark") => {
  const t = tokens[mode] || tokens.dark;

  // Duraciones y animaciones de entrada (#1523) se fusionan aparte: ver motion.js.
  return withMotion(createTheme({
    palette: {
      mode,
      primary: { main: t["color-primary"] },
      secondary: { main: t["color-secondary"] },
      tertiary: { main: t["color-primary-soft"] },
      danger: { main: t["color-danger-deep"] },
      background: {
        default: t["bg-base"],
        dark: t["bg-elevated"],
        light: t["bg-surface"],
        paper: t["bg-elevated"],
        lighter: "#CCCCCC",
        black: t["black-pure"],
        // Roles adicionales
        deep: t["bg-deep"],
        elevated: t["bg-elevated"],
        input: t["bg-input"],
        accordion: t["bg-accordion"],
        hover: t["bg-hover"],
        active: t["bg-active"],
        surface: t["bg-surface"],
        button: t["bg-button"],
      },
      text: {
        primary: t["text-primary"],
        secondary: t["text-secondary"],
        dark: t["text-on-light"],
        muted: t["text-muted"],
        faint: t["text-faint"],
        tertiary: t["text-tertiary"],
      },
      temperature: {
        high: t["temp-high"],
        medium: t["temp-medium"],
        low: t["temp-low"],
        highDark: t["temp-high-dark"],
        mediumDark: t["temp-medium-dark"],
        lowDark: t["temp-low-dark"],
      },
      success: { main: t["color-success"], dark: t["color-success-dark"] },
      warning: { main: t["color-warning"], soft: t["color-warning-soft"] },
      error: { main: t["color-danger"], dark: t["color-danger-strong"] },
      info: { main: t["color-info"], dark: t["color-info-strong"] },
      divider: t["border-soft"],
      tokens: t,
    },
    typography: {
      fontFamily: "Roboto, sans-serif",
      h1: { fontSize: "2rem", fontWeight: 700, color: t["text-primary"] },
      h2: { fontSize: "1.75rem", fontWeight: 700, color: t["text-primary"] },
      h3: { fontSize: "1.5rem", color: t["text-primary"] },
      h4: { fontSize: "1.25rem", color: t["text-primary"] },
      h5: { fontSize: "1rem", color: t["text-primary"] },
      h6: { fontSize: "0.875rem", color: t["text-primary"] },
      subtitle1: { fontSize: "1rem", color: t["text-primary"] },
      subtitle2: { fontSize: "0.875rem", color: t["text-primary"] },
      body1: { fontSize: "1rem", color: t["text-primary"] },
      body2: { fontSize: "0.875rem", color: t["text-primary"] },
      button: { fontSize: "1rem", color: t["text-primary"] },
      caption: { fontSize: "0.75rem", color: t["text-primary"] },
      overline: { fontSize: "0.75rem", color: t["text-primary"] },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: t["color-primary"],
            },
          },
          link: {
            color: t["color-secondary"],
            fontWeight: 500,
            textDecoration: "underline",
            "&:hover, &:active": {
              textDecoration: "underline",
            },
          },
        },
      },
      // El default es el diseño VIGENTE del Turno: botón ghost, sin fondo.
      //
      // Hasta el 09-sep-2026 este override le ponía fondo teal a TODOS los
      // IconButton de la app, así que el look actual había que pedirlo a mano:
      // 65 de los 91 archivos con IconButton escribían `background: transparent`
      // para anularlo. Los que no lo hacían salían con el estilo anterior sin
      // que nadie lo decidiera — los cuadrados rellenos del panel del Resumen y
      // los círculos azules del dictado y del ojo en los cockpits.
      // Es la misma regla que ya vale para el diseño del Turno
      // (`.claude/rules/frontend.md`): un componente nuevo que no sabe nada
      // sale con el look vigente, y lo viejo se pide explícito.
      //
      // El fondo relleno sigue disponible para cuando SÍ significa algo (un
      // estado activo, una acción primaria): `<IconButton data-filled="1">`.
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: t["text-muted"],
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: `rgba(${t["color-info-rgb"]},0.15)`,
              color: t["accent-soft"],
            },
            "&.Mui-disabled": { opacity: 0.35 },
          },
        },
        variants: [
          {
            props: { "data-filled": "1" },
            style: {
              color: t["text-primary"],
              backgroundColor: t["color-primary"],
              "&:hover": { backgroundColor: t["color-primary"] },
            },
          },
        ],
      },
      MuiTable: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              color: "#FFFFFF",
              fontWeight: 700,
              backgroundColor: t["color-primary"],
              border: `1px solid ${t["text-primary"]}`,
            },
            "& .MuiTableCell-body": {
              color: t["text-primary"],
              backgroundColor: t["bg-surface"],
              border: `1px solid ${t["color-primary"]}`,
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: t["bg-accordion"],
            color: t["text-primary"],
            border: `1px solid ${t["color-primary"]}`,
            "&.Mui-expanded": {
              backgroundColor: t["bg-surface"],
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            backgroundColor: t["bg-surface"],
            color: t["text-primary"],
            borderBottom: `1px solid ${t["color-primary"]}`,
            "&.Mui-expanded": {
              minHeight: "48px",
            },
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            backgroundColor: t["bg-surface"],
            color: t["text-primary"],
            padding: "8px 16px",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            color: t["text-primary"],
            backgroundColor: t["bg-surface"],
            borderRadius: "4px",
            "&:focus": {
              backgroundColor: t["bg-surface"],
            },
          },
          icon: {
            color: t["text-primary"],
          },
        },
      },
      // Apagar el overlay de elevación de MUI en TODA superficie flotante: el
      // fondo lo decide el token, no la elevación. Sin esto, `backgroundColor`
      // deja pasar el gradiente y el papel sale bastante más claro de lo que
      // dice el tema (medido: #3a3a3b donde el token pedía #141416).
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      // Barra de estado del iPhone (24-oct-2026): la app instalada dibuja bajo la hora, la isla y el
      // Wi-Fi (viewport-fit=cover + black-translucent en public/index.html). Un diálogo a pantalla
      // completa o un panel lateral sin este margen dejaba la ✕ y los botones de arriba debajo de esos
      // íconos, sin poder tocarlos (ficha 360, visualizador, escáner, pop-ups de /admin/users; #1741).
      // Lo heredan TODOS los Dialog: los `fullScreen` por el Paper (paperFullScreen, abajo) y los demás
      // —centrados o que llenan la pantalla sin `fullScreen` (Paper xs 100%/100vh con `m: 0`, p. ej. la
      // entrega de turno)— por el padding del contenedor (MuiDialog.container, abajo). También los Drawer
      // laterales. Dentro de ellos un componente NO suma otro env(safe-area-inset-top) arriba (quedaría
      // doble). Excepción a propósito: el visor de exámenes del Turno pone `pt: { xs: 0 }` en su
      // contenedor y suma el inset en su cabecera. NO lo heredan: un hijo con position:absolute anclado
      // arriba del Paper (ignora el padding: su `top` suma el inset) ni una capa `position: fixed` propia.
      // Inventario y mediciones: .claude/progress/ficha360-safearea-inventario.md. Solo arriba: el borde
      // inferior lo resuelve cada componente.
      // Diálogos centrados (no fullScreen) en el celular: el margen de 32 px de MUI no cuenta la barra
      // de estado, y cuando el contenido es alto el borde queda en 32 y la ✕ bajo la hora (Vista
      // múltiple, Sincronizar camas, notas del turno…, inventario 26-oct-2026). El inset va como padding
      // del CONTENEDOR (no como margen del Paper): sin barra vale 0 y nada se mueve, y el `m`/`maxHeight`
      // propio de cada Paper sigue igual (un margen en el tema pisaba los `m` planos del sx, revisor
      // 69e73298). `:where()` deja la regla en una sola clase: un `"& .MuiDialog-container": { pt }` en el
      // sx del Dialog la pisa (el visor de exámenes del Turno pone pt 0 y suma el inset en su cabecera).
      // Los fullScreen quedan fuera: ya lo tienen en el Paper (paperFullScreen).
      MuiDialog: {
        styleOverrides: {
          paper: { backgroundImage: "none" },
          paperFullScreen: { paddingTop: "env(safe-area-inset-top)", boxSizing: "border-box" },
          container: {
            "@media (max-width: 768px)": {
              "&:where(:not(:has(> .MuiDialog-paperFullScreen)))": {
                paddingTop: "env(safe-area-inset-top)",
                boxSizing: "border-box",
              },
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paperAnchorLeft: { paddingTop: "env(safe-area-inset-top)", boxSizing: "border-box" },
          paperAnchorRight: { paddingTop: "env(safe-area-inset-top)", boxSizing: "border-box" },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { backgroundImage: "none" },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: t["bg-surface"],
            backgroundImage: "none",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            backgroundColor: t["bg-surface"],
            color: t["text-primary"],
            "&:hover": {
              backgroundColor: t["color-primary"],
            },
            "&.Mui-selected": {
              backgroundColor: t["color-primary"],
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: t["text-primary"],
            "&.Mui-focused": {
              color: t["color-secondary"],
            },
          },
        },
      },
      // Tooltip refinado — reemplaza el gris default de MUI por la estética
      // dark de la app (fondo elevado + borde sutil + radio + sombra suave).
      MuiTooltip: {
        defaultProps: {
          arrow: true,
          enterDelay: 300,
        },
        styleOverrides: {
          tooltip: {
            backgroundColor: t["bg-elevated"],
            color: t["text-primary"],
            border: `1px solid ${t["border-strong"]}`,
            borderRadius: 8,
            fontSize: "0.72rem",
            fontWeight: 600,
            letterSpacing: "0.01em",
            padding: "6px 10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          },
          arrow: {
            color: t["bg-elevated"],
            "&::before": {
              border: `1px solid ${t["border-strong"]}`,
            },
          },
        },
      },
    },
  }));
};

// Compat: el bootstrap antiguo importaba `theme` por default. Lo mantenemos
// para no romper imports en archivos no migrados; resuelve al dark.
const theme = createAppTheme("dark");
export default theme;
